import React, { useState } from 'react';
import { HandThumbUpIcon, CheckBadgeIcon, ShieldCheckIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon, PencilIcon } from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolidIcon } from '@heroicons/react/24/solid';
import RatingStars from './RatingStars';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';

const ReviewCard = ({ review, productId }) => {
  const { token, user } = useAuth();
  const [hasVoted, setHasVoted] = useState(false);
  const [helpfulCount, setHelpfulCount] = useState(review.helpful_count || 0);
  const [votingLoading, setVotingLoading] = useState(false);
  const [showFullImages, setShowFullImages] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editRating, setEditRating] = useState(review.rating);
  const [editBody, setEditBody] = useState(review.review_body);
  const [editLoading, setEditLoading] = useState(false);

  const images = review.images ? (typeof review.images === 'string' ? JSON.parse(review.images) : review.images) : [];
  const adminReply = review.admin_reply ? (typeof review.admin_reply === 'string' ? JSON.parse(review.admin_reply) : review.admin_reply) : null;

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Open gallery
  const openGallery = (index) => {
    setCurrentImageIndex(index);
    setGalleryOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Close gallery
  const closeGallery = () => {
    setGalleryOpen(false);
    document.body.style.overflow = 'unset';
  };

  // Next image
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  // Previous image
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeGallery();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
  };

  // Handle edit submit
  const handleEditSubmit = async () => {
    if (!editBody.trim()) {
      toast.error('Yorum boş olamaz');
      return;
    }

    if (editRating < 1 || editRating > 5) {
      toast.error('Lütfen geçerli bir puan seçin');
      return;
    }

    try {
      setEditLoading(true);
      const response = await api.put(`/api/reviews/${review.id}`, {
        rating: editRating,
        review_body: editBody
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.success) {
        toast.success('Yorumunuz güncellendi ve admin onayına gönderildi. Onaylandıktan sonra görünür olacak.');
        setIsEditing(false);
        // Update local state
        review.rating = editRating;
        review.review_body = editBody;
        review.status = 'pending';
      }
    } catch (error) {
      console.error('Yorum güncelleme hatası:', error);
      toast.error('Yorum güncellenemedi');
    } finally {
      setEditLoading(false);
    }
  };

  // Handle helpful vote
  const handleVote = async () => {
    if (!token) {
      toast.info('Oy vermek için giriş yapmalısınız');
      return;
    }

    if (votingLoading) return;

    try {
      setVotingLoading(true);
      const response = await api.post(`/api/reviews/${review.id}/vote`, {
        vote_type: 'helpful'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.success) {
        if (response.action === 'removed') {
          setHasVoted(false);
          setHelpfulCount(prev => prev - 1);
          toast.success('Oyunuz kaldırıldı');
        } else {
          setHasVoted(true);
          setHelpfulCount(prev => prev + 1);
          toast.success('Oy verildi!');
        }
      }
    } catch (error) {
      console.error('Oy hatası:', error);
      toast.error('Oy kaydedilemedi');
    } finally {
      setVotingLoading(false);
    }
  };

  return (
    <div className="p-6 hover:bg-gray-50 transition-colors duration-200">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-green to-green-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
            {review.author_name?.charAt(0).toUpperCase() || '?'}
          </div>

          {/* Author Info */}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{review.author_name || 'Anonim'}</span>
              {review.is_verified && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-green-100 rounded-full">
                  <CheckBadgeIcon className="w-4 h-4 text-green-600" />
                  <span className="text-xs font-medium text-green-700">Doğrulanmış Alıcı</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <RatingStars rating={review.rating} size="sm" readonly />
              <span className="text-sm text-gray-500">
                {formatDate(review.published_at || review.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Review Text or Edit Form */}
      {isEditing ? (
        <div className="mb-4 space-y-3">
          {/* Rating Edit */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Puan:</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setEditRating(star)}
                  className="text-2xl transition-transform hover:scale-110"
                >
                  {star <= editRating ? '⭐' : '☆'}
                </button>
              ))}
            </div>
          </div>

          {/* Review Body Edit */}
          <textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent resize-none"
            rows={4}
            placeholder="Yorumunuzu yazın..."
          />

          {/* Edit Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleEditSubmit}
              disabled={editLoading}
              className="px-4 py-2 bg-brand-green text-white rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {editLoading ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setEditRating(review.rating);
                setEditBody(review.review_body);
              }}
              disabled={editLoading}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              İptal
            </button>
          </div>
        </div>
      ) : (
        <p className="text-gray-700 leading-relaxed mb-4">
          {review.review_body}
        </p>
      )}

      {/* Images */}
      {images && images.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {images.slice(0, showFullImages ? images.length : 3).map((img, idx) => (
              <img
                key={idx}
                src={img.image_url}
                alt={`Yorum fotoğrafı ${idx + 1}`}
                className="w-24 h-24 object-cover rounded-lg shadow-md hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                onClick={() => openGallery(idx)}
              />
            ))}
            {images.length > 3 && !showFullImages && (
              <button
                onClick={() => setShowFullImages(true)}
                className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 font-semibold hover:bg-gray-200 transition-colors"
              >
                +{images.length - 3}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {galleryOpen && (
        <div 
          className="fixed inset-0 z-[9999] bg-black bg-opacity-95 flex items-center justify-center"
          onClick={closeGallery}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          {/* Close Button */}
          <button
            onClick={closeGallery}
            className="absolute top-4 right-4 z-[10000] w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white transition-all"
          >
            <XMarkIcon className="w-8 h-8" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full text-sm font-medium">
            {currentImageIndex + 1} / {images.length}
          </div>

          {/* Previous Button */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="absolute left-4 z-[10000] w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white transition-all"
            >
              <ChevronLeftIcon className="w-8 h-8" />
            </button>
          )}

          {/* Image */}
          <div className="max-w-[90vw] max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[currentImageIndex].image_url}
              alt={`Yorum fotoğrafı ${currentImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>

          {/* Next Button */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="absolute right-4 z-[10000] w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white transition-all"
            >
              <ChevronRightIcon className="w-8 h-8" />
            </button>
          )}

          {/* Thumbnail Navigation */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-4 py-2 bg-black bg-opacity-50 rounded-full">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(idx);
                  }}
                  className={`w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === currentImageIndex ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img.image_url}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Admin Reply */}
      {adminReply && (
        <div className="mt-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <ShieldCheckIcon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-blue-900">Minela Kids Ekibi</span>
                <span className="text-xs text-blue-600">• Mağaza Yanıtı</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                {adminReply.reply_text}
              </p>
              <span className="text-xs text-gray-500 mt-2 block">
                {formatDate(adminReply.created_at)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
        <div className="flex items-center gap-2">
          <button
            onClick={handleVote}
            disabled={votingLoading}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200
              ${hasVoted 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
              ${votingLoading && 'opacity-50 cursor-not-allowed'}
            `}
          >
            {hasVoted ? (
              <HandThumbUpSolidIcon className="w-5 h-5" />
            ) : (
              <HandThumbUpIcon className="w-5 h-5" />
            )}
            <span>Yararlı</span>
            {helpfulCount > 0 && (
              <span className="bg-white px-2 py-0.5 rounded-full text-sm">
                {helpfulCount}
              </span>
            )}
          </button>

          {/* Edit Button - Only for own reviews */}
          {user && review.user_id === user.id && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-all duration-200"
            >
              <PencilIcon className="w-5 h-5" />
              <span>Düzenle</span>
            </button>
          )}
        </div>

        {images.length > 0 && (
          <div className="flex items-center gap-1 text-sm text-purple-600">
            <span>📷</span>
            <span className="font-medium">{images.length} fotoğraf</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewCard;
